"use client";
import React, { useEffect, useState } from 'react';
import Header from './header';
import Content from './content';
import LegalActView from './legalActView';
import ArticleView from './articleView';
import { Container, ThemeProvider, createTheme, Grid, Typography, Box, Breadcrumbs, Link, Skeleton } from '@mui/material';
import { RecoilRoot } from 'recoil'; // Import RecoilRoot

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

function Page() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [legalActs, setLegalActs] = useState([]);
  const [selectedLegalAct, setSelectedLegalAct] = useState(null);
  const [selectedLegalActTitle, setSelectedLegalActTitle] = useState(null);
  const [articles, setArticles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalHits: 0 });
  const [currentFilters, setCurrentFilters] = useState({}); // Add this state

  // Fetch themes initially
  useEffect(() => {
    if (themes.length === 0 && !isSearching) {
      setLoading(true);
      fetch('https://iids8vsrw0.execute-api.eu-north-1.amazonaws.com/test/ta-get-lists?type=1')
        .then((response) => response.json())
        .then((data) => {
          setThemes(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching themes:', error);
          setLoading(false);
        });
    }
  }, [isSearching]);

  // Fetch legal acts when a theme is selected
  useEffect(() => {
    if (selectedTheme) {
      setLoading(true);
      fetch(`https://iids8vsrw0.execute-api.eu-north-1.amazonaws.com/test/ta-get-lists?type=2&theme=${encodeURIComponent(selectedTheme)}`)
        .then((response) => response.json())
        .then((data) => {
          setLegalActs(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching legal acts:', error);
          setLoading(false);
        });
    }
  }, [selectedTheme]);

  // Fetch articles when a legal act is selected
  useEffect(() => {
    if (selectedLegalAct) {
      setLoading(true);
      fetch(`https://iids8vsrw0.execute-api.eu-north-1.amazonaws.com/test/ta-get-lists?type=3&legal_act_id=${selectedLegalAct}`)
        .then((response) => response.json())
        .then((data) => {
          setArticles(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching articles:', error);
          setLoading(false);
        });
    }
  }, [selectedLegalAct]);

  // Function to handle search, updated to accept query and filter
  const handleSearch = ({ query, filters = {} }, page = 1) => {
    setCurrentQuery(query);
    setCurrentFilters(filters); // Save filters in state
    setLoading(true);
    setIsSearching(true);
    setPagination({ page: 1, totalPages: 1, totalHits: 0 });

    const payload = {
        query,
        filters, // Include filters
        page,    // Page number
    };

    fetch('https://iids8vsrw0.execute-api.eu-north-1.amazonaws.com/test/search-jd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
        .then((response) => response.json())
        .then((data) => {
            setSearchResults(data);
            setPagination({ page: data.page, totalPages: data.total_pages, totalHits: data.total_hits });
            setLoading(false);
        })
        .catch((error) => {
            console.error('Error fetching search results:', error);
            setLoading(false);
        });
};


  // Function to handle page change in pagination
  const handlePageChange = (newPage) => {
    handleSearch({ query: currentQuery, filters: currentFilters }, newPage);
};

  const groupedThemes = themes.reduce((acc, theme) => {
    const firstLetter = theme.theme[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(theme);
    return acc;
  }, {});

  const sortedLetters = Object.keys(groupedThemes).sort();

  const handleBreadcrumbClick = (level) => {
    if (level === 'theme') {
      setSelectedTheme(null);
      setSelectedLegalAct(null);
      setSelectedLegalActTitle(null);
      setIsSearching(false);
    } else if (level === 'legalAct') {
      setSelectedLegalAct(null);
    }
  };

  const handleSelectLegalAct = (legalActId, legalActTitle) => {
    setSelectedLegalAct(legalActId);
    setSelectedLegalActTitle(legalActTitle);
  };
  
  const handleArticleSelection = ({ legal_act_id, article_number }) => {
  // Define new filters based on the selected legal act and article
  const filters = {
    likumaAtsauces: [
      {
        legal_act_id: legal_act_id, // Apply the selected legal act ID
        article_number: article_number, // Apply the selected article number
        likumaAtsauceSlider: [0, 100], // Default slider range
      },
    ],
  };

  // Clear query and apply the new filters
  handleSearch({ query: '', filters });
};

  return (
    <RecoilRoot> {/* Wrap everything inside RecoilRoot */}
      <ThemeProvider theme={darkTheme}>
        <div style={{ backgroundColor: '#1a1a1a', minHeight: '100vh', marginTop: '64px' }}>
          <Header onSearch={handleSearch} />
          <Container maxWidth="lg" style={{ padding: '2rem', marginTop: '0px' }}>
            <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: '1rem' }}>
              <Link color="inherit" href="#" onClick={() => handleBreadcrumbClick('theme')}>
                Themes
              </Link>
              {selectedTheme && (
                <Link color="inherit" href="#" onClick={() => handleBreadcrumbClick('legalAct')}>
                  {selectedTheme}
                </Link>
              )}
              {selectedLegalAct && selectedLegalActTitle && (
                <Typography color="textPrimary">{selectedLegalActTitle}</Typography>
              )}
            </Breadcrumbs>
            {loading ? (
              <Grid container spacing={2}>
                {[...Array(9)].map((_, index) => (
                  <Grid item xs={12} key={index} style={{ marginBottom: '2rem' }}>
                    <Skeleton variant="text" width={150} height={30} style={{ marginBottom: '0.5rem' }} />
                    <Skeleton variant="rectangular" width="100%" height={50} />
                  </Grid>
                ))}
              </Grid>
            ) : isSearching ? (
              <Content searchResults={searchResults} pagination={pagination} onPageChange={handlePageChange} />
            ) : selectedLegalAct ? (
              <ArticleView
  articles={articles}
  selectedLegalActId={selectedLegalAct}
  onSelectArticle={handleArticleSelection}
/>
            ) : selectedTheme ? (
              <LegalActView legalActs={legalActs} onSelectLegalAct={handleSelectLegalAct} />
            ) : (
              <Grid container spacing={2}>
                {sortedLetters.map((letter) => (
  <Grid item xs={12} sm={6} md={4} key={letter} style={{ marginBottom: '2rem' }}>
    <Box>
      {/* Letter heading with white color */}
      <Typography variant="h6" style={{ marginBottom: '1rem', color: '#ffffff' }}>
        {letter}
      </Typography>
      {groupedThemes[letter].map((theme, index) => {
        const maxDocumentCount = Math.max(...themes.map((t) => t.document_count));
        const barWidth = (theme.document_count / maxDocumentCount) * 100;

        return (
          <Box
            key={index}
            style={{ marginBottom: '2rem', cursor: 'pointer' }}
            onClick={() => setSelectedTheme(theme.theme)}
          >
            {/* Theme name and count */}
            <Typography variant="body1" style={{ marginBottom: '0.5rem', color: '#b0b0b0' }}>
              {theme.theme}{' '}
              <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#a0a0a0' }}>
                {theme.document_count}
              </span>
            </Typography>

            {/* Bar chart */}
            <Box style={{ position: 'relative', height: '20px', display: 'flex', alignItems: 'center' }}>
              <Box
                style={{
                  width: `${barWidth}%`,
                  height: '10px',
                  backgroundColor: '#81c784',
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  </Grid>
))}

              </Grid>
            )}
          </Container>
        </div>
      </ThemeProvider>
    </RecoilRoot>
  );
}

export default Page;
